import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

export const MonetizationSection = () => {
  const { format } = useCurrency();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [archivedFilter, setArchivedFilter] = useState('false');

  const load = () => {
    api.get('/admin/monetization').then(({ data }) => setData(data));
    // Monetization view shows up to 500 invoices; server paginates but for now
    // request a large page to preserve existing behaviour.
    api.get('/admin/orders', { params: { archived: archivedFilter, page: 1, page_size: 100 } }).then(({ data }) => setOrders(data.items || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [archivedFilter]);

  const filtered = useMemo(() => {
    if (!search) return orders;
    return orders.filter(o => o.order_number?.toLowerCase().includes(search.toLowerCase()) || o.user?.email?.toLowerCase().includes(search.toLowerCase()));
  }, [orders, search]);

  const archiveInvoice = async (id, archived) => {
    const ok = await confirm({
      title: archived ? 'Archive this invoice?' : 'Restore this invoice?',
      description: archived ? 'It will be hidden from active invoices and revenue KPIs.' : 'It will return to active invoices.',
      confirmLabel: archived ? 'Archive' : 'Restore',
    });
    if (!ok) return;
    try { await api.patch(`/admin/orders/${id}/archive?archived=${archived}`); toast.success(archived ? 'Invoice archived' : 'Invoice restored'); load(); }
    catch { toast.error('Failed'); }
  };

  const archiveAllActive = async () => {
    const ok = await confirm({
      title: 'Archive all active invoices?',
      description: 'All non-archived invoices will move to the archive. Revenue KPIs will reset to zero. You can restore any invoice later.',
      confirmLabel: 'Archive all',
    });
    if (!ok) return;
    try { const { data: r } = await api.post('/admin/orders/archive-all'); toast.success(`Archived ${r.archived} invoices`); load(); }
    catch { toast.error('Failed'); }
  };

  const resetInvoices = async (scope) => {
    const messages = {
      archived: { title: 'Delete all archived invoices?', description: 'Only invoices currently in the archive will be permanently deleted. Active invoices are untouched.', label: 'Delete archived' },
      test: { title: 'Delete all test invoices?', description: 'Every invoice that has not been delivered yet (placed, in production, shipped, cancelled) will be permanently deleted. Delivered orders are preserved.', label: 'Delete test data' },
      all: { title: 'Reset ALL invoices for production?', description: 'This permanently deletes every invoice, resets revenue to zero, and clears order history for all users. This cannot be undone — use this only when moving from testing into production.', label: 'Reset everything' },
    };
    const m = messages[scope];
    const ok = await confirm({ title: m.title, description: m.description, confirmLabel: m.label, variant: 'danger' });
    if (!ok) return;
    try {
      const { data: r } = await api.post(`/admin/orders/reset?scope=${scope}`);
      toast.success(`Deleted ${r.deleted} invoices — ready for production`);
      load();
    } catch { toast.error('Reset failed'); }
  };

  if (!data) return <div style={{ color: 'var(--jw-text-muted)' }}>Loading...</div>;

  const growthColor = data.growth_pct >= 0 ? 'text-emerald-400' : 'text-rose-400';

  return (
    <div className="space-y-5">
      {/* Primary funnel — Placed (Pending) → Recognized → Collected. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="admin-mon-kpi-funnel">
        <Kpi icon={Package} label="Placed (Pending)" value={format(data.placed_pending)} sub={`${data.placed_pending_count || 0} orders awaiting production`} testId="admin-mon-kpi-placed" tone="brand" />
        <Kpi icon={Receipt} label="Revenue Recognized" value={format(data.revenue_recognized)} sub={`${data.revenue_recognized_count || 0} invoices issued`} testId="admin-mon-kpi-recognized" />
        <Kpi icon={DollarSign} label="Cash Collected" value={format(data.cash_collected)} sub={`${data.cash_collected_count || 0} paid invoices · AOV ${format(data.aov || 0)}`} testId="admin-mon-kpi-collected" />
      </div>

      {/* Growth + outstanding overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="jw-card p-5">
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--jw-text-muted)' }}><TrendingUp className="w-4 h-4" /> Growth (MoM)</div>
          <p className={`font-display text-3xl font-bold mt-2 ${growthColor}`} data-testid="admin-mon-growth">{data.growth_pct >= 0 ? '+' : ''}{data.growth_pct}%</p>
          <p className="text-xs mt-1" style={{ color: 'var(--jw-text-subtle)' }}>vs. last month recognized</p>
        </Card>
        <Kpi icon={Receipt} label="This month" value={format(data.this_month.recognized)} testId="admin-mon-kpi-this-month" />
        <Kpi icon={Receipt} label="Last month" value={format(data.last_month.recognized)} testId="admin-mon-kpi-last-month" />
        <Card className="jw-card p-5" data-testid="admin-mon-outstanding">
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--jw-text-muted)' }}><Receipt className="w-4 h-4" /> Outstanding</div>
          <p className="font-display text-3xl font-bold mt-2 text-amber-300">{format(data.outstanding_amount || 0)}</p>
          <p className="text-xs mt-1" style={{ color: 'var(--jw-text-subtle)' }}>{data.outstanding_orders || 0} invoices unpaid</p>
        </Card>
      </div>

      {/* Aging buckets */}
      {data.outstanding_by_age && (
        <Card className="jw-card p-5" data-testid="admin-mon-aging">
          <h3 className="font-semibold mb-3">Invoice aging — unpaid</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {['0-30', '31-60', '61-90', '90+'].map((bucket) => {
              const val = data.outstanding_by_age?.[bucket] || 0;
              const tone = bucket === '0-30' ? 'text-emerald-300' : bucket === '31-60' ? 'text-amber-300' : bucket === '61-90' ? 'text-orange-300' : 'text-rose-300';
              return (
                <div key={bucket} className="p-3 rounded-lg" style={{ background: 'var(--jw-bg-elev)' }} data-testid={`admin-mon-aging-${bucket}`}>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--jw-text-subtle)' }}>{bucket} days</p>
                  <p className={`font-display text-xl font-bold mt-1 ${tone}`}>{format(val)}</p>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        <Card className="jw-card p-5">
          <h3 className="font-semibold mb-3">Recognized vs Collected — last 12 months</h3>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={data.monthly || []}>
                <CartesianGrid stroke="var(--jw-border)" vertical={false} />
                <XAxis dataKey="month" stroke="var(--jw-text-muted)" fontSize={11} />
                <YAxis stroke="var(--jw-text-muted)" fontSize={11} tickFormatter={(v) => format(v)} width={70} />
                <Tooltip contentStyle={{ background: 'var(--jw-bg-alt)', border: '1px solid var(--jw-border)', borderRadius: 8 }} formatter={(v) => format(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                <Bar dataKey="recognized" name="Recognized" fill="url(#jw-brand-grad)" radius={[6, 6, 0, 0]} />
                <Bar dataKey="collected" name="Collected" fill="#10b981" radius={[6, 6, 0, 0]} />
                <defs>
                  <linearGradient id="jw-brand-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E6248A" />
                    <stop offset="100%" stopColor="#FFA500" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="jw-card p-5">
          <h3 className="font-semibold mb-3">Top products</h3>
          <div className="space-y-3">
            {data.top_products.map((p, i) => (
              <div key={p.name} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-md jw-gradient-bg grid place-items-center text-[10px] font-bold text-white">{i + 1}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm truncate">{p.name}</p>
                  <p className="text-[11px]" style={{ color: 'var(--jw-text-subtle)' }}>{p.units} units</p>
                </div>
                <span className="text-sm font-semibold">{format(p.revenue)}</span>
              </div>
            ))}
            {data.top_products.length === 0 && <p className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>No sales yet.</p>}
          </div>
        </Card>
      </div>

      {/* Production/data cleanup moved to Settings → Data cleanup for a unified workflow. */}

      <Card className="jw-card overflow-hidden">
        <div className="p-4 border-b flex flex-wrap items-center gap-2" style={{ borderColor: 'var(--jw-border)' }}>
          <Receipt className="w-4 h-4 text-orange-400" />
          <h3 className="font-semibold">Invoices</h3>
          <span className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{filtered.length} total</span>
          <Select value={archivedFilter} onValueChange={setArchivedFilter}>
            <SelectTrigger className="w-36 ml-2" data-testid="admin-invoices-archived-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="false">Active</SelectItem><SelectItem value="true">Archived</SelectItem></SelectContent>
          </Select>
          <div className="ml-auto relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--jw-text-subtle)' }} />
            <Input placeholder="Search order # or email" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-invoices-search-input" className="pl-9 w-full sm:w-64" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(o => (
                <TableRow key={o.id} data-testid={`admin-invoice-${o.id}-row`}>
                  <TableCell className="font-mono text-xs">{o.order_number}</TableCell>
                  <TableCell>{o.user?.name} <span style={{ color: 'var(--jw-text-subtle)' }}>({o.user?.email})</span></TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{new Date(o.created_at).toLocaleDateString()}</TableCell>
                  <TableCell><Badge variant="secondary" className="capitalize text-xs">{o.status.replace('_', ' ')}</Badge></TableCell>
                  <TableCell className="font-semibold">{format(o.total)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" onClick={() => openInvoice(o.id)} data-testid={`admin-invoice-${o.id}-download-button`} className="jw-btn-gloss-ghost h-8"><FileDown className="w-4 h-4 mr-1" /> Invoice</Button>
                    <Button size="icon" variant="ghost" onClick={() => archiveInvoice(o.id, !o.archived)} title={o.archived ? 'Restore' : 'Archive'} data-testid={`admin-invoice-${o.id}-archive-button`}>
                      {o.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8" style={{ color: 'var(--jw-text-muted)' }}>No invoices.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default MonetizationSection;
